/**
 * YMS Capacity Dashboard Page — Matches Xamarin YMSCapacityDashboardMobilePage.xaml (mobile layout)
 *
 * Displays yard capacity utilization data:
 * - Row 0: Top 3 utilization percentage cards (Total, Yard, Dock Door)
 * - Row 1-2: Yard Slot section (Spaces Open, In Use, Unavailable)
 * - Row 3-4: Dock Door section (Spaces Open, In Use, Unavailable)
 * - Row 5-6: Total section (Spaces Open, In Use, Unavailable)
 *
 * API: manhattan?code={mwFunctionCode}&action=get-yard-capacity&org={orgId}&userId={userId}
 * ViewModel: YMSCapacityDashboardPageViewModel.cs
 */

import { useTranslation } from 'react-i18next';

import { IMAGE_CONSTANTS } from '@/constants';
import { useYardCapacityQuery } from '@/hooks/yardCapacity';

// ─────────────────────────────────────────────────────────────────
// Helpers — Matches Xamarin StringFormat='{0:P2} %' and
// YardCapacityPercentageConverter
// ─────────────────────────────────────────────────────────────────

/** Format percentage string: "45.45" → "45.45 %" (OIC returns pre-calculated percentages) */
function formatPercentage(value: string): string {
  if (!value || value.trim() === '') return '0 %';
  const num = parseFloat(value);
  if (isNaN(num)) return '0 %';
  return `${num.toFixed(2)} %`;
}

/** Format value for "Unavailable" row — integer only, matches YardCapacityPercentageConverter */
function formatUnavailable(value: string): string {
  if (!value || value.trim() === '') return '0';
  const num = parseFloat(value);
  if (isNaN(num)) return '0';
  return String(Math.round(num));
}

/** Format plain numeric value with fallback to "0" */
function formatValue(value: string): string {
  if (!value || value.trim() === '') return '0';
  return value;
}

// ─────────────────────────────────────────────────────────────────
// Top Utilization Card — Matches Xamarin Row 0 (3 columns: .4* / .3* / .3*)
// ─────────────────────────────────────────────────────────────────

interface UtilizationCardProps {
  title: string;
  value: string;
  bgColor: string;
  textColor: string;
}

function UtilizationCard({ title, value, bgColor, textColor }: UtilizationCardProps) {
  return (
    <div
      className="flex min-w-0 flex-col items-center justify-center rounded-[12px] p-[10px] md:p-[18px]"
      style={{ backgroundColor: bgColor }}
    >
      <span
        className="text-center font-poppins text-[10px] md:text-[12px] break-words"
        style={{ color: textColor }}
      >
        {title}
      </span>
      <span
        className="mt-[8px] md:mt-[12px] font-poppins text-[20px] md:text-[34px] font-bold leading-tight"
        style={{ color: textColor }}
      >
        {value}
      </span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Detail Card — Matches Xamarin individual detail cards (Spaces Open, In Use, Unavailable)
// Frame: CornerRadius=12, HasShadow=False, Padding=18(Phone)
// ─────────────────────────────────────────────────────────────────

interface DetailCardProps {
  label: string;
  value: string;
  bgColor: string;
  textColor: string;
}

function DetailCard({ label, value, bgColor, textColor }: DetailCardProps) {
  return (
    <div
      className="flex min-w-0 h-full flex-col items-center justify-center rounded-[12px] p-[10px] md:p-[18px]"
      style={{ backgroundColor: bgColor }}
    >
      <span
        className="text-center font-poppins text-[10px] md:text-[12px]"
        style={{ color: textColor }}
      >
        {label}
      </span>
      <span
        className="mt-[8px] md:mt-[12px] font-poppins text-[20px] md:text-[34px] font-bold"
        style={{ color: textColor }}
      >
        {value}
      </span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Empty State — Matches Xamarin EmptyListView with yard_capacity_someting_went_wrong.svg
// MessageHeader: "Yard capacity utilizations not found"
// ─────────────────────────────────────────────────────────────────

function EmptyCapacityState() {
  const { t } = useTranslation();
  return (
    <div className="flex h-[500px] flex-col items-center justify-center gap-[25px] p-[10px]">
      <img
        src={IMAGE_CONSTANTS.yardCapacityEmpty}
        alt=""
        className="flex-1 object-contain"
        aria-hidden="true"
      />
      {/* Matches Xamarin EmptyListView: PoppinsBold, Phone=20px, TextColor=#243743 */}
      <p className="text-center font-poppins text-[20px] font-bold text-[#243743]">
        {t('ymsCapacity.notFound')}
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Page Component — Matches Xamarin YMSCapacityDashboardPageViewModel
// ─────────────────────────────────────────────────────────────────

export function YMSCapacityDashboardPage() {
  const { t } = useTranslation();
  const { data: capacityData, isSuccess } = useYardCapacityQuery();

  const data = capacityData ?? null;

  // Empty state — Matches Xamarin IsDataNotFound binding (IsVisible={Binding IsDataNotFound, Converter=InverseBool})
  if (!isSuccess || !data) {
    return (
      <div className="px-0 py-[26px]">
        <EmptyCapacityState />
      </div>
    );
  }

  return (
    <div className="flex flex-col px-0 py-[16px] md:py-[26px]">
      {/* Row 0: Top Utilization Cards — Matches Xamarin Grid.ColumnDefinitions=".4*,.3*,.3*" */}
      <div className="grid grid-cols-3 md:grid-cols-[.4fr_.3fr_.3fr] gap-[8px] md:gap-[10px] px-[12px] md:px-[28px]">
        {/* Column 0: Total Capacity Utilization — Orange bg, white text */}
        <UtilizationCard
          title={t('ymsCapacity.totalCapacity')}
          value={formatPercentage(data.TotalCapacityUtilization)}
          bgColor="#E38C25"
          textColor="#FFFFFF"
        />
        {/* Column 1: Yard Capacity Utilization — Light orange bg (25% opacity), orange text */}
        <UtilizationCard
          title={t('ymsCapacity.yardCapacity')}
          value={formatPercentage(data.YardCapacityUtilisation)}
          bgColor="rgba(227, 140, 37, 0.15)"
          textColor="#E38C25"
        />
        {/* Column 2: Dock Door Capacity Utilization — Light orange bg, orange text */}
        <UtilizationCard
          title={t('ymsCapacity.dockDoorCapacity')}
          value={formatPercentage(data.DockDoorCapacityUtilization)}
          bgColor="rgba(227, 140, 37, 0.15)"
          textColor="#E38C25"
        />
      </div>

      {/* 3-column flat grid: rows auto-size to tallest item for consistent heights across languages */}
      <div className="mt-[8px] md:mt-[10px] grid grid-cols-3 gap-x-[8px] gap-y-[6px] md:gap-[10px] px-[12px] md:px-[28px]">
        {/* Row 1: Column Headers */}
        <p className="min-w-0 text-center font-poppins text-[13px] md:text-[16px] font-semibold text-[#243743]">
          {t('ymsCapacity.yardSlot')}
        </p>
        <p className="min-w-0 text-center font-poppins text-[13px] md:text-[16px] font-semibold text-[#243743]">
          {t('ymsCapacity.dockDoor')}
        </p>
        <p className="min-w-0 text-center font-poppins text-[13px] md:text-[16px] font-semibold text-[#243743]">
          {t('ymsCapacity.total')}
        </p>

        {/* Row 2: Spaces Open */}
        <DetailCard
          label={t('ymsCapacity.spacesOpen')}
          value={formatValue(data.YardSpaceOpen)}
          bgColor="rgba(19, 87, 160, 0.06)"
          textColor="#1357A0"
        />
        <DetailCard
          label={t('ymsCapacity.spacesOpen')}
          value={formatValue(data.DockDoorSpaceOpen)}
          bgColor="#CDFAF0"
          textColor="#38796B"
        />
        <DetailCard
          label={t('ymsCapacity.spacesOpen')}
          value={formatValue(data.TotalSpaceOpen)}
          bgColor="#F2F2F2"
          textColor="#516573"
        />

        {/* Row 3: In Use */}
        <DetailCard
          label={t('ymsCapacity.inUse')}
          value={formatValue(data.YardSpaceInUse)}
          bgColor="rgba(19, 87, 160, 0.06)"
          textColor="#1357A0"
        />
        <DetailCard
          label={t('ymsCapacity.inUse')}
          value={formatValue(data.DockDoorSpaceInUse)}
          bgColor="#CDFAF0"
          textColor="#38796B"
        />
        <DetailCard
          label={t('ymsCapacity.inUse')}
          value={formatValue(data.TotalSpaceInUse)}
          bgColor="#F2F2F2"
          textColor="#516573"
        />

        {/* Row 4: Unavailable */}
        <DetailCard
          label={t('ymsCapacity.unavailable')}
          value={formatUnavailable(data.YardSpaceUnavailable)}
          bgColor="rgba(19, 87, 160, 0.06)"
          textColor="#1357A0"
        />
        <DetailCard
          label={t('ymsCapacity.unavailable')}
          value={formatUnavailable(data.DockDoorSpaceUnavailable)}
          bgColor="#CDFAF0"
          textColor="#38796B"
        />
        <DetailCard
          label={t('ymsCapacity.unavailable')}
          value={formatUnavailable(data.TotalSpaceUnavailable)}
          bgColor="#F2F2F2"
          textColor="#516573"
        />
      </div>
    </div>
  );
}
